import { computed } from "vue";
import { useAuth } from "@/shared/composables/useAuth";

/**
 * The control center's sections, in rail order, and the system entitlement each needs — the
 * client's copy of `entitlements::system::CONTROL_CENTER` on the server, which checks them on every
 * request regardless. Holding any one opens the control center; Runtime, Maintenance, Settings and
 * License are all `manage_server`.
 *
 * The ORDER is the rail's, not the server's: sections are grouped there (people, content, server),
 * and the rail groups what is adjacent, so Departments sits with Users and System Roles — who may
 * do what, and in which organisational unit — rather than where the server lists its entitlement.
 *
 * In `shared/` because the shell's account menu offers the control center from every section.
 */
export const CONTROL_CENTER_SECTIONS = [
	{ id: "users", entitlement: "system:manage_users" },
	{ id: "roles", entitlement: "system:manage_roles" },
	{ id: "departments", entitlement: "system:manage_departments" },
	{ id: "projects", entitlement: "system:manage_projects" },
	{ id: "dataspaces", entitlement: "system:manage_dataspaces" },
	{ id: "releases", entitlement: "system:manage_releases" },
	{ id: "service-tokens", entitlement: "system:manage_service_tokens" },
	{ id: "runtime", entitlement: "system:manage_server" },
	{ id: "maintenance", entitlement: "system:manage_server" },
	{ id: "settings", entitlement: "system:manage_server" },
	{ id: "license", entitlement: "system:manage_server" },
] as const;

export type ControlCenterSection = (typeof CONTROL_CENTER_SECTIONS)[number]["id"];

/** What the signed-in user's system role lets them do, from their profile. */
export function useSystemAccess() {
	const { user } = useAuth();

	const granted = computed(() => new Set(user.value?.system_entitlements ?? []));

	/** The sections this user may open, in rail order. */
	const sections = computed(() =>
		CONTROL_CENTER_SECTIONS.filter((s) => granted.value.has(s.entitlement)).map((s) => s.id),
	);

	return {
		/** Whether the profile has loaded — before it, nothing can be decided. */
		known: computed(() => user.value !== null),
		can: (entitlement: string) => granted.value.has(entitlement),
		canOpen: (section: ControlCenterSection) => sections.value.includes(section),
		sections,
		canOpenControlCenter: computed(() => sections.value.length > 0),
	};
}
